import { API as LegacyAPI } from "../legacy/api";
import { LegacyEvent } from "../legacy/api";
import { SpinButtonPosition } from "./api";

/**
 * API Event Mapping.
 *
 * This object maps the new object properties to the old string events.
 *
 * @param event The old API string event.
 * @param property The new API object property.
 * @param guard Evaluates whether or not to emit an event. Gets the value as parameter unmodified.
 * @param transform Transforms the original value to be compatible with the legacy API.
 * @param pre Executed before the legacy event, with the value (transformed) as parameter.
 * @param post Executed after the legacy event, with the value (transformed) as parameter.
 */
export interface IAPIEventMapping {
  event: LegacyEvent;
  property: string;
  guard?: (value: any) => boolean;
  transform?: (value: any) => any;
  pre?: (value: any) => void;
  post?: (value: any) => void;
}

export const mappings: IAPIEventMapping[] = [
  {
    event: "theme.reset",
    property: "theme",
    guard: value => value === null
  },
  {
    event: "theme.set",
    property: "theme",
    guard: value => value !== null
  },
  {
    event: "activity.hide",
    property: "activityIndicator.enabled",
    guard: value => value === false
  },
  {
    event: "activity.show",
    property: "activityIndicator.enabled",
    guard: value => value === true
  },
  {
    event: "autoplay.available.set",
    property: "autoplay",
    guard: value => value === null,
    transform: () => false
  },
  {
    event: "autoplay.available.set",
    property: "autoplay",
    guard: value => value !== null,
    transform: () => true
  },
  { event: "autoplay.settings.set", property: "autoplay.settings" },
  { event: "autoplay.roundsvalues.set", property: "autoplay.rounds.values" },
  {
    event: "autoplay.rounds.reset",
    property: "autoplay.rounds.selected",
    guard: value => value === null
  },
  {
    event: "autoplay.rounds.set",
    property: "autoplay.rounds.selected",
    guard: value => value !== null
  },
  { event: "autoplay.roundsleft.set", property: "autoplay.rounds.left" },
  {
    event: "autoplay.start",
    property: "autoplay.running",
    guard: value => value === true
  },
  {
    event: "autoplay.stop",
    property: "autoplay.running",
    guard: value => value === false
  },
  {
    event: "autoplaybutton.disable",
    property: "autoplay.enabled",
    guard: value => value === false
  },
  {
    event: "autoplaybutton.enable",
    property: "autoplay.enabled",
    guard: value => value === true
  },
  {
    event: "betbutton.disable",
    property: "bet.enabled",
    guard: value => value === false
  },
  {
    event: "betbutton.enable",
    property: "bet.enabled",
    guard: value => value === true
  },
  {
    event: "clock.available.set",
    property: "clock",
    guard: value => value === null,
    transform: () => false
  },
  {
    event: "clock.available.set",
    property: "clock",
    guard: value => value !== null,
    transform: () => true
  },
  {
    event: "clock.date.set",
    property: "clock.date",
    transform: value => new Date(value)
  },
  { event: "gamesettings.set", property: "settings.settings" },
  {
    event: "dialog.hide",
    property: "dialog.enabled",
    guard: value => value === false
  },
  {
    event: "dialog.show",
    property: "dialog",
    guard: value => value.enabled === true
  },
  {
    event: "gamehistory.enabled",
    property: "gameHistory.enabled",
    guard: value => typeof value === "boolean"
  },
  {
    event: "gamehistory.available.set",
    property: "gameHistory",
    guard: value => value === null,
    transform: () => false
  },
  {
    event: "gamehistory.available.set",
    property: "gameHistory",
    guard: value => value !== null,
    transform: () => true
  },
  {
    event: "gamerules.content.set",
    property: "gameRules.content",
    guard: value => value !== ""
  },
  {
    event: "gamerules.available.set",
    property: "gameRules",
    guard: value => value === null,
    transform: () => false
  },
  {
    event: "gamerules.available.set",
    property: "gameRules",
    guard: value => value !== null,
    transform: () => true
  },
  {
    event: "lefthandmode.enable",
    property: "leftHandMode.value",
    guard: value => value === true
  },
  {
    event: "lefthandmode.disable",
    property: "leftHandMode.value",
    guard: value => value === false
  },
  {
    event: "menubutton.disable",
    property: "menu.enabled",
    guard: value => value === false
  },
  {
    event: "menubutton.enable",
    property: "menu.enabled",
    guard: value => value === true
  },
  {
    event: "playforfun.show",
    property: "playForFunIndicator.enabled",
    guard: value => value === true
  },
  {
    event: "playforfun.hide",
    property: "playForFunIndicator.enabled",
    guard: value => value === false
  },
  {
    event: "home.available.set",
    property: "home",
    guard: value => value === null,
    transform: () => false
  },
  {
    event: "home.available.set",
    property: "home",
    guard: value => value !== null,
    transform: () => true
  },
  {
    event: "home.enabled.set",
    property: "home.enabled"
  },
  {
    event: "plugin.available.set",
    property: "plugin",
    guard: value => value === null,
    transform: () => false
  },
  {
    event: "plugin.available.set",
    property: "plugin",
    guard: value => value !== null,
    transform: () => true
  },
  {
    event: "plugin.enabled.set",
    property: "plugin.enabled"
  },
  { event: "pluginmenubutton.icon.set", property: "plugin.icon" },
  {
    event: "quickspin.available.set",
    property: "quickSpin",
    guard: value => value === null,
    transform: () => false
  },
  {
    event: "quickspin.available.set",
    property: "quickSpin",
    guard: value => value !== null,
    transform: () => true
  },
  { event: "quickspin.enabled.set", property: "quickSpin.enabled" },
  { event: "quickspin.active.set", property: "quickSpin.active" },
  {
    event: "soundbutton.disable",
    property: "volume.enabled",
    guard: value => value === false
  },
  {
    event: "soundbutton.enable",
    property: "volume.enabled",
    guard: value => value === true
  },
  {
    event: "sound.mute",
    property: "volume.muted",
    guard: value => value === true
  },
  {
    event: "sound.unmute",
    property: "volume.muted",
    guard: value => value === false
  },
  {
    event: "spin.start",
    property: "spin.spinning",
    guard: value => value === true
  },
  {
    event: "spin.idle",
    property: "spin.spinning",
    guard: value => value === false
  },
  {
    event: "spinbutton.disable",
    property: "spin.enabled",
    guard: value => value === false
  },
  {
    event: "spinbutton.enable",
    property: "spin.enabled",
    guard: value => value === true
  },
  {
    event: "fullscreen.canvas.activate",
    property: "spin.buttonPosition",
    guard: value => value === SpinButtonPosition.DOCKED
  },
  {
    event: "fullscreen.canvas.deactivate",
    property: "spin.buttonPosition",
    guard: value => value === SpinButtonPosition.DEFAULT
  },
  {
    event: "ticker.message.set",
    property: "ticker.message"
  },
  {
    event: "ticker.hide",
    property: "ticker.enabled",
    guard: value => value === false
  },
  {
    event: "ticker.show",
    property: "ticker.enabled",
    guard: value => value === true
  },
  {
    event: "ticker.animation.start",
    property: "ticker.animate",
    guard: value => value === true
  },
  {
    event: "ticker.animation.stop",
    property: "ticker.animate",
    guard: value => value === false
  },
  {
    event: "ticker.message.set",
    property: "ticker",
    guard: value => value === null,
    transform: () => " ",
    post: () => LegacyAPI.emit("ticker.hide")
  },
  { event: "gamename.set", property: "gameName" },
  { event: "gameproducer.set", property: "gameProducer" },
  {
    event: "volume.set",
    property: "volume.value"
  }
];
